/* eslint-disable max-classes-per-file */
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { Md5 } from "ts-md5";
import { environment } from "../../environments/environment";
import {
    NameValueAmountDto,
    PagedList
} from "../modules/shared/models/api-response.model";
import { buildExportFilename } from "../modules/shared/utils/export-helpers";
import { AuthService } from "./auth.service";

const API_URL = environment.api;

interface IInvoiceUser {
    userId: number;
    accountId: string;
    email: string;
    firstName: string;
    lastName: string;
}

export type OutstandingFeesReportDto = {
    resultSet: PagedList<Invoice>;
    totalBalance: number;
};

export class PagedTransactionHistoryResponse {
    list: PagedList<TransactionHistoryItem>;
    premiumPaid = 0;
    premiumRefunded = 0;
    premiumNet = 0;
}

export type TransactionHistoryStats = {
    issued: number;
    paid: number;
    cancelled: number;
    refunded: number;
    voided: number;
    net: number;
};

export type PaymentHistoryStats = {
    series: NameValueAmountDto[];
    premiumPaid: number;
    premiumRefunded: number;
    premiumNet: number;
};

export class Invoice {
    accountId: string;
    invoiceId: number;
    invoiceType: string;
    invoiceNumber: string;
    total: number;
    premiumPaid: number;
    premiumRefunded: number;
    accountName: string;
    paymentNumber: string;
    createDate: Date;
    invoiceDepartmentTypes: string;
    invoiceCollateralTypes: string;
    trimmedAccountId: string;
    paymentMethod: string;

    static fromJSON(obj): Invoice {
        let temp = new Invoice();
        Object.assign(temp, obj);

        if (obj.invoiceNumber) {
            temp.invoiceNumber = obj.invoiceNumber.replace(/^.+-/, "");
        }

        if (obj.createDate) {
            temp.createDate = new Date(obj.createDate);
        }
        if (obj.invoiceDepartmentTypes === null) {
            temp.invoiceDepartmentTypes = "N/A";
        }
        if (obj.invoiceCollateralTypes === null) {
            temp.invoiceCollateralTypes = "N/A";
        }

        return temp;
    }
}

export class InvoiceType {
    code: string;
    name: string;

    static fromJSON(obj): InvoiceType {
        let temp = new InvoiceType();
        Object.assign(temp, obj);
        return temp;
    }
}

export class CollateralType {
    code: string;
    name: string;

    static fromJSON(obj): CollateralType {
        let temp = new CollateralType();
        Object.assign(temp, obj);
        return temp;
    }
}

export class DepartmentType {
    code: string;
    name: string;

    static fromJSON(obj): DepartmentType {
        let temp = new DepartmentType();
        Object.assign(temp, obj);
        return temp;
    }
}

export class CustomType {
    code: string;
    name: string;

    static fromJSON(obj): CustomType {
        let temp = new CustomType();
        Object.assign(temp, obj);
        return temp;
    }
}

export class PolicyAction {
    policyId: number;
    accountId: string;
    loanId: string;
    collateralId: string;
    policyNumber: string;
    effectiveDate: Date;
    expirationDate: Date;
    issueDate: Date;
    currentBalance: number;
    currentPremium: number;
    loanNumber: string;
    borrowerName: string;
    collateralType: string;
    collateralDescription: string;
    actionStatus: string;
    vin: string;
    collateralTypeCode: string;
    departmentTypeCode: string;
    customTypeCode: string;
    reasonCode: string;
    reasonName: string;

    hashedId: string;

    static fromJSON(obj): PolicyAction {
        const temp = new PolicyAction();
        Object.assign(temp, obj);

        if (obj.effectiveDate) {
            temp.effectiveDate = new Date(obj.effectiveDate);
        }
        if (obj.expirationDate) {
            temp.expirationDate = new Date(obj.expirationDate);
        }
        if (obj.issueDate) {
            temp.issueDate = new Date(obj.issueDate);
        }

        // Building a hash as a unique trackById to assist in maintaining
        // selection across pagination in Outstanding Premiums, using same
        // values as we're joining on in OutstandingPremiumSubmissions;
        // A collision here likely means other issues with duplicate records
        // in Outstanding Premiums instead of a true hash collision.

        const md5 = new Md5();
        md5.appendStr(obj.accountId)
            .appendStr(obj.collateralId)
            .appendStr(obj.policyId)
            .appendStr(obj.policyNumber);

        temp.hashedId = md5.end(false) as string;

        return temp;
    }
}

export class InvoicedPolicy {
    invoicedPolicyId: number;
    invoiceId: number;
    invoiceNumber: string;
    loanId: string;
    collateralId: string;
    invoiceType: string;
    policyInvoiceType: string;
    policyId: number;
    accountId: string;
    loanNumber: string;
    policyNumber: string;
    paymentNumber: string;
    paidDate: Date;
    departmentTypeCode: string;
    collateralTypeCode: string;
    customTypeCode: string;
    createDate: Date;
    effectiveDate: Date;
    expirationDate: Date;
    currentBalance: number;
    currentPremium: number;
    borrowerName: string;
    collateralType: string;
    collateralDescription: string;
    collateralVin: string;

    static fromJSON(obj): InvoicedPolicy {
        let temp = new InvoicedPolicy();
        Object.assign(temp, obj);

        if (obj.paidDate) {
            temp.paidDate = new Date(obj.paidDate);
        }
        if (obj.createDate) {
            temp.createDate = new Date(obj.createDate);
        }
        if (obj.effectiveDate) {
            temp.effectiveDate = new Date(obj.effectiveDate);
        }
        if (obj.expirationDate) {
            temp.expirationDate = new Date(obj.expirationDate);
        }

        return temp;
    }
}

export class TransactionHistoryItem {
    accountId: string;
    borrowerName: string;
    cancelType: string;
    collateralDescription: number;
    loanId: string;
    loanNumber: string;
    loanOfficerCode: string;
    loanOfficerName: string;
    policyNumber: string;
    transactionAmount: number;
    transactionDate: Date;
    transactionTypeCode: string;
    transactionTypeName: string;
    coverageTypeCode: string;
    coverageTypeName: string;
    insuranceStatusName: string;
    insuranceStatusCode: string;
    incDate: Date;
    expDate: Date;

    static fromJSON(obj): TransactionHistoryItem {
        const temp = new TransactionHistoryItem();
        Object.assign(temp, obj);

        if (obj.transactionDate) {
            temp.transactionDate = new Date(obj.transactionDate);
        }

        if (obj.incDate) {
            temp.incDate = new Date(obj.incDate);
        }

        if (obj.expDate) {
            temp.expDate = new Date(obj.expDate);
        }

        return temp;
    }
}

export class InvoicedFee {
    accountId: string;
    trimmedAccountId: string;
    createDate: Date;
    createdAtString: string;
    departmentTypeCodes: string;
    coverageTypeCodes: string;
    amountType: string;
    itemCount: number;
    balance: number | null;
    rate: number;
    total: number;
    invoiceType: string;
    invoiceId: number;
    isPaid: boolean;
    minimum: number;
    maximum: number;
    details: string;

    static fromJSON(obj): InvoicedFee {
        let temp = new InvoicedFee();
        Object.assign(temp, obj);

        if (obj.createDate) {
            temp.createDate = new Date(obj.createDate);
        }

        return temp;
    }
}

@Injectable()
export class AccountingService {
    constructor(
        public http: HttpClient,
        private _authService: AuthService
    ) {}

    private get _defaultOptions() {
        return { headers: this._headers };
    }
    private get _headers(): HttpHeaders {
        let h = new HttpHeaders();
        h = h.set("Authorization", `bearer ${this._authService.accessToken}`);
        h = h.set("Content-Type", "application/json");
        return h;
    }

    getInvoices(
        offset = 0,
        limit = 20,
        sort: string | null = null,
        search: string | null = null,
        dateRange: Partial<{ begin: Date; end: Date }> | null = null,
        invoiceTypes: string[] | null = null,
        lenders: string[] | null = null,
        departments: string[] | null = null,
        collaterals: string[] | null = null
    ): Observable<PagedList<Invoice>> {
        const p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] =
                dateRange.begin.toISOString().split("T")[0] + "T00:00:00Z";
            p["end-at"] =
                dateRange.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (invoiceTypes && invoiceTypes.length > 0) {
            p["invoice-types"] = invoiceTypes.join(",");
        }

        if (lenders && lenders.length > 0) {
            p["lenders"] = lenders.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (collaterals && collaterals.length > 0) {
            p["collaterals"] = collaterals.join(",");
        }

        if (!sort) {
            sort = "-createDate";
        } else {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        const options = { headers: this._headers, params: p };

        return this.http.get(`${API_URL}/api/v1/invoices`, options).pipe(
            map((res: PagedList<Invoice>) => {
                res.offset = res.offset / res.limit;
                res.data = res.data.map(Invoice.fromJSON);
                return res;
            })
        );
    }

    getInvoiceStats(
        search: string | null = null,
        dateRange: Partial<{ begin: Date; end: Date }> | null = null,
        invoiceTypes: string[] | null = null,
        lenders: string[] | null = null,
        departments: string[] | null = null,
        collaterals: string[] | null = null
    ) {
        const p = {};

        if (search) {
            p["filter"] = search;
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] =
                dateRange.begin.toISOString().split("T")[0] + "T00:00:00Z";
            p["end-at"] =
                dateRange.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (invoiceTypes && invoiceTypes.length > 0) {
            p["invoice-types"] = invoiceTypes.join(",");
        }

        if (lenders && lenders.length > 0) {
            p["lenders"] = lenders.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (collaterals && collaterals.length > 0) {
            p["collaterals"] = collaterals.join(",");
        }

        const options = { headers: this._headers, params: p };

        return this.http.get<PaymentHistoryStats>(
            `${API_URL}/api/v1/invoices/stats`,
            options
        );
    }

    exportInvoices(
        columns,
        format,
        sort: string | null = null,
        search: string | null = null,
        dateRange: any = null,
        invoiceTypes: any | null = null,
        lenders: any[] | null = null,
        departments: string[] | null = null,
        collaterals: string[] | null = null,
        paymentMethod: string[] | null = null,
        showDeletedFees = false,
        outstandingFees = false
    ): Observable<Blob> {
        let p = { format: format };

        if (search) {
            p["filter"] = search;
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] =
                dateRange.begin.toISOString().split("T")[0] + "T00:00:00Z";
            p["end-at"] =
                dateRange.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (invoiceTypes && invoiceTypes.length > 0) {
            p["invoice-types"] = invoiceTypes.join(",");
        }

        if (lenders && lenders.length > 0) {
            p["lenders"] = lenders.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (collaterals && collaterals.length > 0) {
            p["collaterals"] = collaterals.join(",");
        }

        if (paymentMethod && paymentMethod.length > 0) {
            p["payment-method"] = paymentMethod.join(",");
        }

        if (!sort) {
            sort = "-createDate";
        } else {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        if (showDeletedFees) {
            p["closed"] = showDeletedFees;
        }

        if (outstandingFees) {
            p["outstanding-fees"] = outstandingFees;
        }

        let options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };
        return this.http
            .post<Blob>(`${API_URL}/api/v1/invoices/export`, columns, options)
            .pipe(
                map((res) => {
                    let url = window.URL.createObjectURL(res);
                    let a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename(
                        "invoice_report",
                        this._authService.claims.accountName,
                        format
                    );
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    getInvoice(invoiceId: string): Observable<Invoice> {
        return this.http
            .get(
                `${API_URL}/api/v1/invoices/${invoiceId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res = Invoice.fromJSON(res);
                    return res;
                })
            );
    }

    exportInvoice(columns, format, invoiceId): Observable<Blob> {
        let options = {
            headers: this._headers,
            responseType: "blob" as "json",
            params: { format: format }
        };

        return this.http
            .post<Blob>(
                `${API_URL}/api/v1/invoices/${invoiceId}/invoiced-policies/export`,
                columns,
                options
            )
            .pipe(
                map((res) => {
                    let url = window.URL.createObjectURL(res);
                    let a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename(
                        `invoice_${invoiceId}`,
                        this._authService.claims.accountName,
                        format
                    );
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    getInvoicedPolicies(invoiceId: string): Observable<InvoicedPolicy[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/invoices/${invoiceId}/invoiced-policies`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res = res.map(InvoicedPolicy.fromJSON);
                    return res;
                })
            );
    }

    getPolicyActions(
        filter: string | null = null,
        accounts: string[] | null = null,
        forcePlacedReasons: string[] | null = null,
        departments: string[] | null = null,
        coverages: string[] | null = null,
        issueDate: Partial<{ begin: Date; end: Date }> | null = null,
        paymentMethod: string[] | null = null,
        invoiceCreatedBy: string[] | null = null,
        invoiceType: string[] | null = null,
        sort: string | null = null,
        offset = 0,
        limit = 20,
        closedOnly = false
    ): Observable<PagedList<PolicyAction>> {
        const p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (filter) {
            p["filter"] = filter;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (forcePlacedReasons) {
            p["reasons"] = forcePlacedReasons.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (issueDate && issueDate.begin && issueDate.end) {
            p["start-at"] = issueDate.begin.toISOString().split("T")[0];
            p["end-at"] = issueDate.end.toISOString().split("T")[0];
        }

        if (paymentMethod && paymentMethod.length > 0) {
            p["payment-method"] = paymentMethod.join(",");
        }

        if (invoiceCreatedBy && invoiceCreatedBy.length > 0) {
            p["invoice-created-by"] = invoiceCreatedBy.join(",");
        }

        if (invoiceType && invoiceType.length > 0) {
            p["invoice-type"] = invoiceType.join(",");
        }

        if (closedOnly) {
            p["loan-statuses"] = "D,P,T,t,W";
        } else {
            p["loan-statuses"] = "A,X,Y,Z";
        }

        if (!sort) {
            sort = "issueDate";
            p["sort"] = sort;
        } else {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        const options = { headers: this._headers, params: p };

        return this.http
            .get(`${API_URL}/api/v1/accounts/policy-actions`, options)
            .pipe(
                map((res: PagedList<PolicyAction>) => {
                    res.offset = res.offset / res.limit;
                    res.data = res.data.map(PolicyAction.fromJSON);
                    return res;
                })
            );
    }

    getPolicyActionsStats(
        filter: string | null = null,
        accounts: string[] | null = null,
        forcePlacedReasons: string[] | null = null,
        departments: string[] | null = null,
        coverages: string[] | null = null,
        issueDate: Partial<{ begin: Date; end: Date }> | null = null,
        paymentMethod: string[] | null = null,
        invoiceCreatedBy: string[] | null = null,
        invoiceType: string[] | null = null,
        closedOnly = false
    ): Observable<NameValueAmountDto> {
        const p = {};

        if (filter) {
            p["filter"] = filter;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (forcePlacedReasons) {
            p["reasons"] = forcePlacedReasons.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (issueDate && issueDate.begin && issueDate.end) {
            p["start-at"] = issueDate.begin.toISOString().split("T")[0];
            p["end-at"] = issueDate.end.toISOString().split("T")[0];
        }

        if (paymentMethod && paymentMethod.length > 0) {
            p["payment-method"] = paymentMethod.join(",");
        }

        if (invoiceCreatedBy && invoiceCreatedBy.length > 0) {
            p["invoice-created-by"] = invoiceCreatedBy.join(",");
        }

        if (invoiceType && invoiceType.length > 0) {
            p["invoice-type"] = invoiceType.join(",");
        }

        if (closedOnly) {
            p["loan-statuses"] = "D,P,T,t,W";
        } else {
            p["loan-statuses"] = "A,X,Y,Z";
        }

        const options = { headers: this._headers, params: p };

        return this.http.get<NameValueAmountDto>(
            `${API_URL}/api/v1/accounts/policy-actions/stats`,
            options
        );
    }

    exportPolicyActions(
        format,
        policies: PolicyAction[] = []
    ): Observable<Blob> {
        let p = {
            accountName: this._authService.claims.accountName,
            format: format,
            outstandingPremiums: policies.map((x) => {
                return {
                    accountId: x.accountId,
                    loanNumber: x.loanNumber,
                    borrowerName: x.borrowerName,
                    policyNumber: x.policyNumber,
                    collateralType: x.collateralType,
                    currentPremium: x.currentPremium,
                    departmentTypeCode: x.departmentTypeCode
                };
            })
        };

        let options = {
            headers: this._headers,
            responseType: "blob" as "json"
        };
        return this.http
            .post<Blob>(
                `${API_URL}/api/v1/accounts/policy-actions/export`,
                p,
                options
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    let url = window.URL.createObjectURL(res);
                    let a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename(
                        "request_to_invoice",
                        this._authService.claims.accountName,
                        format
                    );
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    requestToInvoicePolicyActions(items: PolicyAction[]): Observable<void> {
        const rows = items.map((elem) => {
            return {
                accountId: elem.accountId,
                policyId: elem.policyId,
                action: "invoice"
            };
        });

        return this.http.post<void>(
            `${API_URL}/api/v1/accounts/policy-actions`,
            rows,
            this._defaultOptions
        );
    }

    requestToVoidPolicyActions(items: PolicyAction[]): Observable<void> {
        const rows = items.map((elem) => {
            return {
                accountId: elem.accountId,
                policyId: elem.policyId,
                action: "void"
            };
        });

        return this.http.post<void>(
            `${API_URL}/api/v1/accounts/policy-actions`,
            rows,
            this._defaultOptions
        );
    }

    invoiceLoans(items: PolicyAction[]): Observable<any> {
        const rows = items.map((elem) => {
            return { policyId: elem.policyId, action: "invoice" };
        });

        return this.http
            .post(
                `${API_URL}/api/v1/suspense-policy-actions`,
                rows,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    voidLoans(items: PolicyAction[]): Observable<any> {
        const rows = items.map((elem) => {
            return { policyId: elem.policyId, action: "void" };
        });

        return this.http
            .post(
                `${API_URL}/api/v1/suspense-policy-actions`,
                rows,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getCustomTypes(accountId: string): Observable<CustomType[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/custom-types/${accountId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => CustomType.fromJSON(elem));
                })
            );
    }

    getCustomTypeFilters(): Observable<CustomType[]> {
        return this.http
            .get(`${API_URL}/api/v1/custom-types/filters`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => CustomType.fromJSON(elem));
                })
            );
    }

    getCollateralTypes(
        accountId: string | null = null
    ): Observable<CollateralType[]> {
        let p = {};

        if (accountId) {
            p["accounts"] = accountId;
        }

        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/collateral-types`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => CollateralType.fromJSON(elem));
                })
            );
    }

    getDepartmentTypes(accountId: string): Observable<DepartmentType[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/department-types/${accountId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => DepartmentType.fromJSON(elem));
                })
            );
    }

    getDepartmentTypeFilters(): Observable<DepartmentType[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/department-types/filters`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => DepartmentType.fromJSON(elem));
                })
            );
    }

    getInsuranceTypes(
        exclude: string | null = null
    ): Observable<{ name: string; code: string }[]> {
        let p = {};

        if (exclude) {
            p["exclude"] = exclude;
        }

        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/insurance-statuses`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getInvoiceTypes(): Observable<InvoiceType[]> {
        return this.http
            .get(`${API_URL}/api/v1/invoice-types`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => InvoiceType.fromJSON(elem));
                })
            );
    }

    getFeeInvoiceTypes(): Observable<InvoiceType[]> {
        return this.http
            .get(`${API_URL}/api/v1/invoice-types/fees`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => InvoiceType.fromJSON(elem));
                })
            );
    }

    getAllInvoiceTypes(): Observable<InvoiceType[]> {
        return this.http
            .get(`${API_URL}/api/v1/invoice-types/all`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => InvoiceType.fromJSON(elem));
                })
            );
    }

    getProgramTypes(): Observable<string[]> {
        return this.http
            .get(`${API_URL}/api/v1/program-types`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getTransactionTypes(): Observable<string[]> {
        return this.http
            .get(`${API_URL}/api/v1/transaction-types`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getTransactionHistory(
        sort: string | null = null,
        search: string | null = null,
        dateRange: Partial<{ begin: Date; end: Date }> | null = null,
        lenders: string[] | null = null,
        forcePlacedReasons: string[] | null = null,
        transactionTypes: string[] | null = null,
        insuranceStatuses: string[] | null,
        offset = 0,
        limit = 20,
        coverages: string[] | null
    ): Observable<PagedTransactionHistoryResponse> {
        const p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] =
                dateRange.begin.toISOString().split("T")[0] + "T00:00:00Z";
            p["end-at"] =
                dateRange.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (lenders && lenders.length > 0) {
            p["lenders"] = lenders.join(",");
        }

        if (forcePlacedReasons) {
            p["reasons"] = forcePlacedReasons.join(",");
        }

        if (transactionTypes && transactionTypes.length > 0) {
            p["transaction-types"] = transactionTypes.join(",");
        }

        if (insuranceStatuses && insuranceStatuses.length > 0) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        if (!sort) {
            sort = "-createDate";
            p["sort"] = sort;
        } else {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        const options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/transaction-history`, options)
            .pipe(
                map((res: any) => {
                    res.list.offset = res.list.offset / res.list.limit;
                    res.list.data = res.list.data.map(
                        TransactionHistoryItem.fromJSON
                    );
                    return res;
                })
            );
    }

    getTransactionHistoryStats(
        search: string | null = null,
        dateRange: Partial<{ begin: Date; end: Date }> | null = null,
        lenders: string[] | null = null,
        forcePlacedReasons: string[] | null = null,
        transactionTypes: string[] | null = null,
        insuranceStatuses: string[] | null,
        coverages: string[] | null
    ) {
        const p = {};

        if (search) {
            p["filter"] = search;
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] =
                dateRange.begin.toISOString().split("T")[0] + "T00:00:00Z";
            p["end-at"] =
                dateRange.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (lenders && lenders.length > 0) {
            p["lenders"] = lenders.join(",");
        }

        if (forcePlacedReasons) {
            p["reasons"] = forcePlacedReasons.join(",");
        }

        if (transactionTypes && transactionTypes.length > 0) {
            p["transaction-types"] = transactionTypes.join(",");
        }

        if (insuranceStatuses && insuranceStatuses.length > 0) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        const options = { headers: this._headers, params: p };

        return this.http.get<TransactionHistoryStats>(
            `${API_URL}/api/v1/transaction-history/stats`,
            options
        );
    }

    exportTransactionHistory(
        columns,
        format,
        sort: string | null = null,
        search: string | null = null,
        dateRange: Partial<{ begin: Date; end: Date }> | null = null,
        lenders: any[] | null = null,
        forcePlacedReasons: string[] | null = null,
        transactionTypes: any[] | null = null,
        insuranceStatuses: string[] | null,
        premiumRefunded: number = 0.0,
        premiumPaid: number = 0.0,
        premiumNet: number = 0.0,
        coverages: string[] | null
    ): Observable<Blob> {
        let p = { format: format };
        let body = {
            columns: columns,
            premiumRefunded: premiumRefunded,
            premiumPaid: premiumPaid,
            premiumNet: premiumNet
        };

        if (search) {
            p["filter"] = search;
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] =
                dateRange.begin.toISOString().split("T")[0] + "T00:00:00Z";
            p["end-at"] =
                dateRange.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (lenders && lenders.length > 0) {
            p["lenders"] = lenders.join(",");
        }

        if (forcePlacedReasons) {
            p["reasons"] = forcePlacedReasons.join(",");
        }

        if (transactionTypes && transactionTypes.length > 0) {
            p["transaction-types"] = transactionTypes.join(",");
        }

        if (insuranceStatuses && insuranceStatuses.length > 0) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        if (coverages && coverages.length > 0) {
            p["coverages"] = coverages.join(",");
        }

        if (!sort) {
            sort = "-createDate";
            p["sort"] = sort;
        } else {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        let options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };
        return this.http
            .post<Blob>(
                `${API_URL}/api/v1/transaction-history/export`,
                body,
                options
            )
            .pipe(
                map((res) => {
                    let url = window.URL.createObjectURL(res);
                    let a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename(
                        "transaction_history",
                        this._authService.claims.accountName,
                        format
                    );
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    undeleteFee(invoiceId: any): Observable<any> {
        let options = { headers: this._headers, observe: "response" } as any;
        return this.http
            .post(
                `${API_URL}/api/v1/invoices/${invoiceId}/invoiced-fees/undelete`,
                null,
                options
            )
            .pipe(shareReplay());
    }

    deleteFee(invoiceId: any): Observable<any> {
        let options = { headers: this._headers, observe: "response" } as any;
        return this.http
            .delete(
                `${API_URL}/api/v1/invoices/${invoiceId}/invoiced-fees`,
                options
            )
            .pipe(shareReplay());
    }

    markFeePaid(invoiceId: number): Observable<boolean> {
        let options = { headers: this._headers, observe: "response" } as any;
        return this.http
            .post(
                `${API_URL}/api/v1/invoices/${invoiceId}/invoiced-fees/mark-paid`,
                null,
                options
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.ok;
                })
            );
    }

    getInvoicedFeeList(invoiceId: string): Observable<InvoicedFee[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/invoices/${invoiceId}/invoiced-fees/`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res = res.map(InvoicedFee.fromJSON);
                    return res;
                })
            );
    }

    exportInvoicedFee(columns, format, invoiceId): Observable<Blob> {
        let options = {
            headers: this._headers,
            params: { format: format },
            responseType: "blob" as "json"
        };
        return this.http
            .post<Blob>(
                `${API_URL}/api/v1/invoices/${invoiceId}/invoiced-fees/export`,
                columns,
                options
            )
            .pipe(
                map(
                    (res) => {
                        let url = window.URL.createObjectURL(res);
                        let a = document.createElement("a");
                        document.body.appendChild(a);
                        a.setAttribute("style", "display: none");
                        a.href = url;
                        a.download = buildExportFilename(
                            "fee_invoice",
                            this._authService.claims.accountName,
                            format
                        );
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();

                        return res;
                    },
                    (err) => {}
                )
            );
    }

    getOutstandingFees(
        offset: number = 0,
        limit: number = 20,
        sort: string | null = null,
        search: string | null = null,
        dateRange: any = null,
        invoiceTypes: any | null = null,
        lenders: any[] | null = null,
        departments: string[] | null = null,
        collaterals: string[] | null = null,
        paymentMethod: string[] | null = null,
        showDeletedFees = false
    ): Observable<OutstandingFeesReportDto> {
        let p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }

        if (dateRange !== null && dateRange.begin && dateRange.end) {
            p["start-at"] =
                dateRange.begin.toISOString().split("T")[0] + "T00:00:00Z";
            p["end-at"] =
                dateRange.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (invoiceTypes && invoiceTypes.length > 0) {
            p["invoice-types"] = invoiceTypes.join(",");
        }

        if (lenders && lenders.length > 0) {
            p["lenders"] = lenders.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (collaterals && collaterals.length > 0) {
            p["collaterals"] = collaterals.join(",");
        }

        if (paymentMethod && paymentMethod.length > 0) {
            p["payment-method"] = paymentMethod.join(",");
        }

        if (!sort) {
            sort = "-createDate";
        } else {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        if (showDeletedFees) {
            p["closed"] = showDeletedFees;
        }

        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/invoices/outstanding-fees`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: OutstandingFeesReportDto) => {
                    res.resultSet.offset =
                        res.resultSet.offset / res.resultSet.limit;
                    res.resultSet.data = res.resultSet.data.map(
                        Invoice.fromJSON
                    );
                    return res;
                })
            );
    }
}
